import {
    createDisposableTimeout,
    Disposable,
    type Disposable as DisposableHandle,
} from '@patsnap/tahiti-core';

export interface ShowWriteDiffRequest {
    documentId: string;
    toolCallId: string;
    beforeContentId?: string;
    afterContentId?: string;
}

export type WriteDiffTarget = (
    request: ShowWriteDiffRequest,
) => void | Promise<void>;

/** 将写入差异请求路由到对应文档渲染器。 */
export interface WriteDiffCoordinator {
    /**
     * 注册指定文档的渲染目标。
     * @param documentId 文档 ID
     * @param target 差异渲染处理器
     * @returns 注销目标的 Disposable
     */
    registerTarget(documentId: string, target: WriteDiffTarget): DisposableHandle;

    /**
     * 请求展示文档写入差异；目标尚未挂载时保留最新请求。
     * @param request 差异请求
     * @returns 目标处理完成后返回 true
     * @throws 目标长期未挂载、请求被替换或目标处理失败
     */
    request(request: ShowWriteDiffRequest): Promise<boolean>;
}

export class WriteDiffCoordinatorError extends Error {
    constructor(
        readonly code: 'TARGET_TIMEOUT' | 'REQUEST_REPLACED',
        message: string,
    ) {
        super(message);
        this.name = 'WriteDiffCoordinatorError';
    }
}

interface PendingWriteDiff {
    request: ShowWriteDiffRequest;
    resolve: (delivered: boolean) => void;
    reject: (error: Error) => void;
    timeout: DisposableHandle;
}

/** application scope 的显式路由实现，替代跨 renderer 广播。 */
export class InMemoryWriteDiffCoordinator implements WriteDiffCoordinator {
    private readonly targets = new Map<string, WriteDiffTarget>();
    private readonly pending = new Map<string, PendingWriteDiff>();

    constructor(private readonly targetTimeoutMs = 5_000) {}

    registerTarget(documentId: string, target: WriteDiffTarget): DisposableHandle {
        this.targets.set(documentId, target);
        const pending = this.pending.get(documentId);
        if (pending) {
            this.pending.delete(documentId);
            pending.timeout.dispose();
            void Promise.resolve(target(pending.request))
                .then(() => pending.resolve(true))
                .catch(pending.reject);
        }

        return Disposable.create(() => {
            if (this.targets.get(documentId) === target) {
                this.targets.delete(documentId);
            }
        });
    }

    async request(request: ShowWriteDiffRequest): Promise<boolean> {
        const target = this.targets.get(request.documentId);
        if (!target) {
            const previous = this.pending.get(request.documentId);
            if (previous) {
                previous.timeout.dispose();
                previous.reject(new WriteDiffCoordinatorError(
                    'REQUEST_REPLACED',
                    `文档 ${request.documentId} 的差异请求已被更新请求替换`,
                ));
            }

            return new Promise<boolean>((resolve, reject) => {
                const timeout = createDisposableTimeout(() => {
                    const pending = this.pending.get(request.documentId);
                    if (pending?.request !== request) return;
                    this.pending.delete(request.documentId);
                    reject(new WriteDiffCoordinatorError(
                        'TARGET_TIMEOUT',
                        `文档 ${request.documentId} 的差异渲染器未及时挂载`,
                    ));
                }, this.targetTimeoutMs);
                this.pending.set(request.documentId, {
                    request,
                    resolve,
                    reject,
                    timeout,
                });
            });
        }
        await target(request);
        return true;
    }
}
